import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmBarrelMakeClient, FirearmBarrelMakeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmBarrelMakeClient = () => new FirearmBarrelMakeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useFirearmBarrelMakes = () =>
  useQuery({
    queryKey: ['firearmBarrelMakes'],
    queryFn: async () => {
      const response = await getFirearmBarrelMakeClient().searchFirearmBarrelMakes({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmBarrelMakeSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmBarrelMakeClient().searchFirearmBarrelMakes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmBarrelMakeById = (id: string) =>
  useQuery({
    queryKey: ['firearmBarrelMake', id],
    queryFn: async () => {
      const response = await getFirearmBarrelMakeClient().getFirearmBarrelMakeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmBarrelMake = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmBarrelMakeDTO) => {
      await getFirearmBarrelMakeClient().createFirearmBarrelMake(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmBarrelMakes'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmBarrelMake = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmBarrelMakeDTO }) => {
      await getFirearmBarrelMakeClient().updateFirearmBarrelMake(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmBarrelMakes'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmBarrelMake = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmBarrelMakeClient().deleteFirearmBarrelMake(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmBarrelMakes'] });
    },
  });
};